import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { fn } from '@ember/helper';
import { t } from 'ember-intl';
import Switch from 'tio-reimagined/components/switch';

export interface ShouldIRefinanceComponentSignature {
  Args: {
    recommendation: (arg0: boolean) => void;
  };
}

class ShouldIRefinanceComponent extends Component<ShouldIRefinanceComponentSignature> {
  @tracked choice1 = false;
  @tracked choice2 = false;
  @tracked choice3 = false;
  @tracked choice4 = false;
  @tracked choice5 = false;
  @tracked choice6 = false;
  @tracked choice7 = false;
  @tracked choice8 = false;

  setChoice = (key: string, value: boolean) => {
    // @ts-expect-error dynamic key assignment
    this[key] = value;

    const recommend =
      this.choice1 &&
      this.choice2 &&
      this.choice3 &&
      this.choice4 &&
      !this.choice5 &&
      !this.choice6 &&
      !this.choice7 &&
      !this.choice8;

    this.args?.recommendation(recommend);
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <div class="space-y-5">
      <div class="tio-row-top-loose">
        <div class="tio-row-tight shrink-0 pt-0.5">
          <span class="text-xs tio-fg-disabled uppercase w-6 text-center">No</span>
          <Switch
            data-test-id="credit-score-switch"
            @checked={{this.choice1}}
            @onChange={{fn this.setChoice "choice1"}}
            @size="sm"
          />
          <span class="text-xs tio-fg-disabled uppercase w-6 text-center">Yes</span>
        </div>
        <div>
          <h4 class="font-semibold tio-fg-primary">
            {{t "wellness_insights.refinancing_good_credit_score"}}
          </h4>
          <p class="text-sm tio-fg-secondary">{{t
              "wellness_insights.refinancing_good_credit_score_description"
            }}</p>
        </div>
      </div>

      <div class="tio-row-top-loose">
        <div class="tio-row-tight shrink-0 pt-0.5">
          <span class="text-xs tio-fg-disabled uppercase w-6 text-center">No</span>
          <Switch
            data-test-id="stable-income-switch"
            @checked={{this.choice2}}
            @onChange={{fn this.setChoice "choice2"}}
            @size="sm"
          />
          <span class="text-xs tio-fg-disabled uppercase w-6 text-center">Yes</span>
        </div>
        <div>
          <h4 class="font-semibold tio-fg-primary">
            {{t "wellness_insights.refinancing_stable_income"}}
          </h4>
          <p class="text-sm tio-fg-secondary">{{t
              "wellness_insights.refinancing_stable_income_description"
            }}</p>
        </div>
      </div>

      <div class="tio-row-top-loose">
        <div class="tio-row-tight shrink-0 pt-0.5">
          <span class="text-xs tio-fg-disabled uppercase w-6 text-center">No</span>
          <Switch
            data-test-id="loan-balance-switch"
            @checked={{this.choice3}}
            @onChange={{fn this.setChoice "choice3"}}
            @size="sm"
          />
          <span class="text-xs tio-fg-disabled uppercase w-6 text-center">Yes</span>
        </div>
        <div>
          <h4 class="font-semibold tio-fg-primary">
            {{t "wellness_insights.refinancing_loan_balance_at_least"}}
          </h4>
          <p class="text-sm tio-fg-secondary">{{t
              "wellness_insights.refinancing_loan_balance_at_least_description"
            }}</p>
        </div>
      </div>

      <div class="tio-row-top-loose">
        <div class="tio-row-tight shrink-0 pt-0.5">
          <span class="text-xs tio-fg-disabled uppercase w-6 text-center">No</span>
          <Switch
            data-test-id="interest-lower-switch"
            @checked={{this.choice4}}
            @onChange={{fn this.setChoice "choice4"}}
            @size="sm"
          />
          <span class="text-xs tio-fg-disabled uppercase w-6 text-center">Yes</span>
        </div>
        <div>
          <h4 class="font-semibold tio-fg-primary">
            {{t "wellness_insights.refinancing_interest_be_lower"}}
          </h4>
          <p class="text-sm tio-fg-secondary">{{t
              "wellness_insights.refinancing_interest_be_lower_description"
            }}</p>
        </div>
      </div>

      <hr class="tio-border-default" />

      <div class="tio-row-top-loose">
        <div class="tio-row-tight shrink-0 pt-0.5">
          <span class="text-xs tio-fg-disabled uppercase w-6 text-center">No</span>
          <Switch
            data-test-id="federal-loans-trouble-switch"
            @checked={{this.choice5}}
            @onChange={{fn this.setChoice "choice5"}}
            @size="sm"
          />
          <span class="text-xs tio-fg-disabled uppercase w-6 text-center">Yes</span>
        </div>
        <div>
          <h4 class="font-semibold tio-fg-primary">
            {{t "wellness_insights.refinancing_have_federal_loans_trouble"}}
          </h4>
          <p class="text-sm tio-fg-secondary">{{t
              "wellness_insights.refinancing_have_federal_loans_trouble_description"
            }}</p>
        </div>
      </div>

      <div class="tio-row-top-loose">
        <div class="tio-row-tight shrink-0 pt-0.5">
          <span class="text-xs tio-fg-disabled uppercase w-6 text-center">No</span>
          <Switch
            data-test-id="loan-forgiveness-switch"
            @checked={{this.choice6}}
            @onChange={{fn this.setChoice "choice6"}}
            @size="sm"
          />
          <span class="text-xs tio-fg-disabled uppercase w-6 text-center">Yes</span>
        </div>
        <div>
          <h4 class="font-semibold tio-fg-primary">
            {{t "wellness_insights.refinancing_eligible_loan_forgiveness"}}
          </h4>
          <p class="text-sm tio-fg-secondary">{{t
              "wellness_insights.refinancing_eligible_loan_forgiveness_description"
            }}</p>
        </div>
      </div>

      <div class="tio-row-top-loose">
        <div class="tio-row-tight shrink-0 pt-0.5">
          <span class="text-xs tio-fg-disabled uppercase w-6 text-center">No</span>
          <Switch
            data-test-id="interest-rate-comfortable-switch"
            @checked={{this.choice7}}
            @onChange={{fn this.setChoice "choice7"}}
            @size="sm"
          />
          <span class="text-xs tio-fg-disabled uppercase w-6 text-center">Yes</span>
        </div>
        <div>
          <h4 class="font-semibold tio-fg-primary">
            {{t "wellness_insights.refinancing_interest_rate_comfortable"}}
          </h4>
          <p class="text-sm tio-fg-secondary">{{t
              "wellness_insights.refinancing_interest_rate_comfortable_description"
            }}</p>
        </div>
      </div>

      <div class="tio-row-top-loose">
        <div class="tio-row-tight shrink-0 pt-0.5">
          <span class="text-xs tio-fg-disabled uppercase w-6 text-center">No</span>
          <Switch
            data-test-id="back-to-school-switch"
            @checked={{this.choice8}}
            @onChange={{fn this.setChoice "choice8"}}
            @size="sm"
          />
          <span class="text-xs tio-fg-disabled uppercase w-6 text-center">Yes</span>
        </div>
        <div>
          <h4 class="font-semibold tio-fg-primary">
            {{t "wellness_insights.refinancing_back_to_school"}}
          </h4>
          <p class="text-sm tio-fg-secondary">{{t
              "wellness_insights.refinancing_back_to_school_description"
            }}</p>
        </div>
      </div>
    </div>
  </template>
}

export default ShouldIRefinanceComponent;
